/**
 * usePWAInstall — gère la détection de la plateforme et l'événement
 * beforeinstallprompt sur Android/Chrome desktop.
 *
 * Renvoie :
 *  - `platform` : 'ios' | 'android-chrome' | 'desktop-chrome' | 'other'
 *  - `canPrompt` : true si on peut déclencher l'install natif (Android/Chrome)
 *  - `isStandalone` : true si l'app tourne déjà en mode installé
 *  - `promptInstall()` : déclenche le natif (sinon noop)
 *  - `dismissedAt` / `dismiss()` : permet de cacher le prompt pendant 7j
 */
import { useEffect, useState, useCallback } from 'react';

type Platform = 'ios' | 'android-chrome' | 'desktop-chrome' | 'other';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const DISMISS_KEY = 'nia-pwa-install-dismissed';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !(window as { MSStream?: unknown }).MSStream;
  if (isIOS) return 'ios';
  const isAndroid = /Android/i.test(ua);
  const isChrome = /Chrome|CriOS/i.test(ua);
  if (isAndroid && isChrome) return 'android-chrome';
  if (isChrome) return 'desktop-chrome';
  return 'other';
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  if ((navigator as { standalone?: boolean }).standalone === true) return true;
  // Other browsers
  return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
}

export function usePWAInstall() {
  const [platform] = useState<Platform>(() => detectPlatform());
  const [isStandalone] = useState<boolean>(() => detectStandalone());
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    const stored = localStorage.getItem(DISMISS_KEY);
    if (!stored) return false;
    const ts = parseInt(stored, 10);
    return Date.now() - ts < SEVEN_DAYS_MS;
  });

  useEffect(() => {
    function onBeforeInstall(e: Event): void {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'dismissed') {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setDismissed(true);
    }
    return choice.outcome;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  /** Should we show the install prompt? */
  const shouldShow =
    !isStandalone &&
    !dismissed &&
    (platform === 'ios' || platform === 'android-chrome' || !!deferredPrompt);

  return {
    platform,
    isStandalone,
    canPrompt: !!deferredPrompt,
    shouldShow,
    promptInstall,
    dismiss,
  };
}
